import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  SFPageContainer,
  SFSkeleton,
  SFCardBody,
  SFGridContainer,
  SFGridContainerItem,
  SFGridItem,
  SFDivider,
  SFActionBar,
} from "@sf-core";
import { toastActions, layoutActions } from "@base-provider";
import { getUserTenantRole } from "../../../services/tenantUserService";
import EditUserTenantForm from "./editUserTenantForm";
import { ACTION_TYPE } from "../../../constants/actionType";
import {
  editTenantUser,
  deleteTenantUser,
  restoreTenantUser,
} from "../../../services/tenantUserService";
import getActionDefs from "./actionDefs";
import ActiveChip from "../../../components/activeChip";
import DatedAction from "../../../components/datedAction";
import { PATHS } from "../../../constants/paths";

const EditUserTenant = () => {
  const { tenantId, id } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    data: {
      tenantUserId: null,
      tenantId: null,
      tenantUserRoleId: null,
      expiresAt: null,
    },
    errors: [],
  });

  useEffect(() => {
    dispatch(layoutActions.changeBreadcrumbName(null));
    getUserTenantRole(id, tenantId).then((res) => {
      setForm({
        data: res.data,
        errors: null,
      });
      dispatch(
        layoutActions.changeBreadcrumbName({
          breadcrumbName: res.data.tenantName,
        })
      );
    });
  }, []);

  const onAction = async (type) => {
    switch (type) {
    case ACTION_TYPE.EDIT:
      if (form.errors.length === 0) {
        await editTenantUser({
          id: form.data.userId,
          tenantUserId: form.data.tenantUserId,
          tenantId: form.data.tenantId,
          tenantUserRoleId: form.data.tenantUserRoleId,
          expiresAt: form.data.expiresAt,
        });
        dispatch(
          toastActions.showSuccessMessage(
            t("tenantUsersScreen.messages.editSuccess")
          )
        );
        navigate(PATHS.ADMIN_USERS_USER_PROFILE.replace(/:id/, id));
      } else {
        dispatch(
          toastActions.showErrorMessage(
            t("tenantUsersScreen.messages.invalidFieldsMessage")
          )
        );
      }
      break;
    case ACTION_TYPE.DELETE:
      await deleteTenantUser({
        id: form.data.userId,
        tenantId: form.data.tenantId,
      });
      dispatch(
        toastActions.showSuccessMessage(
          t("tenantUsersScreen.messages.deleteSuccess")
        )
      );
      navigate(PATHS.ADMIN_USERS_USER_PROFILE.replace(/:id/, id));
      break;
    case ACTION_TYPE.RESTORE:
      if (form.errors.length === 0) {
        await restoreTenantUser({
          id: form.data.userId,
          tenantId: form.data.tenantId,
          expiresAt: form.data.expiresAt,
        });
        dispatch(
          toastActions.showSuccessMessage(
            t("tenantUsersScreen.messages.restoreSuccess")
          )
        );
        navigate(PATHS.ADMIN_USERS_USER_PROFILE.replace(/:id/, id));
      } else {
        dispatch(
          toastActions.showErrorMessage(
            t("tenantUsersScreen.messages.invalidFieldsMessage")
          )
        );
      }
      break;
    default:
      break;
    }
  };

  const actionDefs = getActionDefs({
    t,
    onCancelClick: () =>
      navigate(PATHS.ADMIN_USERS_USER_PROFILE.replace(/:id/, id)),
    onSaveClick: onAction.bind(this, ACTION_TYPE.EDIT),
    onDeleteClick: onAction.bind(this, ACTION_TYPE.DELETE),
    onRestoreClick: onAction.bind(this, ACTION_TYPE.RESTORE),
    formData: form.data,
  });

  if (!form.data.tenantUserId) {
    return <SFSkeleton />;
  }

  return (
    <>
      <SFPageContainer disableGutters>
        <SFActionBar
          actionDefs={actionDefs}
          titleProps={{
            title: t("adminRoutes.titles.editTenant"),
          }}
        />
        <SFCardBody>
          <SFGridContainer spacing={2}>
            <SFGridItem xs={12}>
              <EditUserTenantForm
                data={form.data}
                onChange={(data, errors) => setForm({ data, errors })}
              />
            </SFGridItem>
            <SFGridItem xs={12}>
              <ActiveChip active={form.data.isActive} />
            </SFGridItem>
            <SFGridItem xs={12}>
              <SFDivider />
            </SFGridItem>
            <SFGridContainerItem>
              <SFGridItem xs={6}>
                <DatedAction
                  user={form.data.createdBy}
                  date={form.data.createdAt}
                  label={t("tenantUsersScreen.tenantUsersForm.createdByLabel")}
                />
              </SFGridItem>
              <SFGridItem xs={6}>
                <DatedAction
                  user={form.data.updatedBy}
                  date={form.data.updatedAt}
                  label={t("tenantUsersScreen.tenantUsersForm.updatedByLabel")}
                />
              </SFGridItem>
              <SFGridItem xs={6}>
                <DatedAction
                  user={form.data.deletedBy}
                  date={form.data.deletedAt}
                  label={t("tenantUsersScreen.tenantUsersForm.deletedByLabel")}
                />
              </SFGridItem>
            </SFGridContainerItem>
          </SFGridContainer>
        </SFCardBody>
      </SFPageContainer>
    </>
  );
};

export default EditUserTenant;
